import React from "react";
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  Typography,
} from "@mui/material";

import { Partner, PartnerResource } from "../../api/partners";

interface Props {
  /** Currently selected partner id, or undefined when the meeting is open. */
  value?: string;
  /** Emits the partner id, or undefined when the scope is cleared. */
  onChange: (partnerId: string | undefined) => void;
}

/**
 * Marks a meeting as ONE partner's "Partner-only" (private, partner-scoped) at
 * creation time, writing Meeting.partnerId.
 *
 * Deliberately the private half of the calendar's PartnerAssociationSection and
 * nothing more. "Preferred" is keyed by a meeting's STABLE session key
 * (recurring master id, else the gcal event id) — a meeting created here has no
 * gcal event, so it has no such key and cannot be preferred. Offering the
 * toggle would be an affordance that could never be honoured.
 *
 * Unlike the calendar section, this one holds no state of its own beyond the
 * partner list: the value lives in the create form's data and is submitted with
 * the rest of it, so there is nothing to persist separately.
 */
export const PartnerScopeField: React.FC<Props> = ({ value, onChange }) => {
  const [loading, setLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState(false);
  const [partners, setPartners] = React.useState<Partner[]>([]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchError(false);

    PartnerResource.getListCached()
      .then((ps) => {
        if (cancelled) return;
        setPartners(ps);
      })
      .catch(() => {
        if (!cancelled) setFetchError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Derived from the form's own value rather than mirrored in local state, so
  // the switch can never say "on" while the form carries no scope. Three
  // states, one source of truth:
  //   undefined -> off            ""  -> on, nothing picked yet
  //   "<id>"    -> on, scoped to that partner
  // The empty string is why the parent's validity check must reject it: it is
  // the "on but incomplete" state, and submitting it would create an open
  // meeting while the admin believes it is private.
  const partnerOnly = value !== undefined;

  const handleToggle = (checked: boolean) => {
    onChange(checked ? "" : undefined);
  };

  if (fetchError) {
    return (
      <Alert severity="warning">
        Could not load partners, so this meeting can&apos;t be made
        partner-only. It will be created open — check your connection and try
        again.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={160} height={24} />
        <Skeleton variant="rounded" height={40} sx={{ mt: 1.5 }} />
      </Box>
    );
  }

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={partnerOnly}
            onChange={(e) => handleToggle(e.target.checked)}
          />
        }
        label="Partner-only (private)"
      />
      <FormHelperText sx={{ mt: -0.5, mb: 1, ml: 0 }}>
        Only one partner&apos;s members (plus Admins/Hosts) can see and join.
        This replaces the visibility setting above — a partner-only meeting is
        hidden from everyone else whether or not it is publicly visible.
      </FormHelperText>
      {partnerOnly && (
        <FormControl fullWidth size="small" required error={!value}>
          <InputLabel id="vp-partner-private-label">Partner</InputLabel>
          <Select
            labelId="vp-partner-private-label"
            label="Partner"
            value={value ?? ""}
            // Empty stays "" (on, unselected) rather than collapsing to
            // undefined, which would silently flip the switch back off.
            onChange={(e) => onChange(e.target.value)}
          >
            {partners.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.displayName}
              </MenuItem>
            ))}
          </Select>
          {!value && (
            <FormHelperText>
              Select a partner, or turn Partner-only off.
            </FormHelperText>
          )}
        </FormControl>
      )}
      {partners.length === 0 && partnerOnly && (
        <Typography variant="caption" color="text.secondary">
          No partners exist yet.
        </Typography>
      )}
    </Box>
  );
};
